/**
 * DealInspectorPane — RIGHT pane (320px fixed) in the Inbox Cockpit.
 * Metadata + action buttons for the selected deal.
 *
 * Authority: WI-UI-CLEANUP-001 Stage 2, SA-4.
 * Design tokens: DS-CARD-V5 from ui-invariants.js — zero inline hex.
 *
 * Props:
 *   - selected:   InboxItem | null
 *   - onRefresh:  () => void — called after successful actions so the inbox re-fetches
 */

import React, { useState } from 'react';
import {
  ACTION_BUTTONS,
  DS_COLORS,
  DS_TYPOGRAPHY,
} from '@/config/ui-invariants';
import { AGENT_CONFIG, COCKPIT_LABELS, WORK_TYPES } from '@/config/task-display-config';
import { rejectCampaignTask, request } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CaretDown } from '@phosphor-icons/react';
// ── The pass-reason vocabulary (Stage A, WI-THE-IDEA-LANE-END-TO-END-001) ──────
//
// `pass_reason` CLASSIFIES a deal, and free-form text is never permitted in a
// classification field (docs/taxonomies/ACCREZEO_TAXONOMY.md). This pane used to send a raw
// textarea to the same endpoint the centre pane's dropdown writes to, so two surfaces wrote
// one field and only one of them was controlled. It is read from the SAME config block
// DealCard.jsx reads, so the two controls cannot offer different lists.
//
// Read WITHOUT a `|| []` fallback, deliberately. DealCard.jsx:82 does
// `(PASS_ACTION.dropdown && PASS_ACTION.dropdown.items) || []`, which silently renders an
// EMPTY menu if the config key is ever renamed — a classification field quietly offering
// nothing. Here an empty list is surfaced to the CEO as a refusal instead (see the render
// below), and the server refuses the write in the same case.
const PASS_REASON_ITEMS =
  ((AGENT_CONFIG.deal_sourcing || {}).actions || []).find((a) => a.id === 'pass')
    ?.dropdown?.items || [];
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// ── Simple toast helper (avoids external sonner dependency assumption) ─────────
// DECISION: Using a lightweight inline toast via a DOM insertion to avoid
// importing sonner if it's not available. Falls back gracefully.
function showToast(message, type = 'success') {
  if (typeof window === 'undefined') return;
  // Use sonner if available on window, else console
  if (window.__sonner_toast) {
    type === 'success'
      ? window.__sonner_toast.success(message)
      : window.__sonner_toast.error(message);
  } else {
    // Fallback: simple aria-live region approach
    console.info(`[Toast][${type}] ${message}`);
  }
}

// Try to import sonner toast — optional peer
let toast;
try {
  // Dynamic-style conditional — bundler tree-shakes if not present
  // DECISION: Wrapping in try/catch so missing sonner doesn't crash the component.
  ({ toast } = require('sonner')); // noscan
} catch (_) {
  toast = null;
}

function fireToast(message, type = 'success') {
  if (toast) {
    type === 'success' ? toast.success(message) : toast.error(message);
  } else {
    showToast(message, type);
  }
}

// ── Style constants ───────────────────────────────────────────────────────────

const MICRO_LABEL_STYLE = {
  ...DS_TYPOGRAPHY.microLabel,
  color: DS_COLORS.text.muted,
  marginBottom: '10px',
  display: 'block',
};

const ROW_LABEL_STYLE = {
  ...DS_TYPOGRAPHY.sectionLabel,
  color: DS_COLORS.text.secondary,
  fontSize: '12px',
  letterSpacing: '0.04em',
  textTransform: 'none',
  fontWeight: 600,
};

const ROW_VALUE_STYLE = {
  ...DS_TYPOGRAPHY.bodyMd,
  color: DS_COLORS.text.primary,
  marginLeft: '8px',
};

// ── Sub-components ────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div
      style={{
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        height: '100%',
      }}
    >
      <p
        style={{
          color: DS_COLORS.text.muted,
          fontSize: '16px',
          fontFamily: 'Inter, system-ui, sans-serif',
          margin: 0,
          textAlign: 'center',
        }}
      >
        Select a deal to see details
      </p>
    </div>
  );
}

function MicroLabel({ children }) {
  return <span style={MICRO_LABEL_STYLE}>{children}</span>;
}

function TaxonomyRow({ label, value }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: '8px',
      }}
    >
      <span style={ROW_LABEL_STYLE}>{label}</span>
      <span style={ROW_VALUE_STYLE}>{value || '—'}</span>
    </div>
  );
}

// ── Founders section ──────────────────────────────────────────────────────────

function FoundersSection({ selected }) {
  const isTracxn = selected.work_type === WORK_TYPES.TRACXN_LIVEDEALS || selected.work_type === WORK_TYPES.FIRST_LOOK;
  const foundersFromResearch = selected.payload?.founders_from_research;
  const foundersCowork = selected.payload?.founders;

  // RC1 fix (WI-LIVEDEALS-FOUNDER-IDENTITY-MISMATCH-RCA-001): the Cowork-verified
  // founders[] always win over the unverified founders_from_research (Perplexity)
  // guesses — mirroring the deal-sourcing DealCard precedence
  // (founders[] before founders_from_research). Previously this pane rendered the
  // research guess FIRST for Tracxn deals, surfacing the wrong person's name and
  // link even when the verified founder sat on the same record.
  if (foundersCowork && foundersCowork.length > 0) {
    return (
      <div data-testid="founders-section">
        {foundersCowork.map((f, i) => {
          // Cowork founder objects carry full_name (canonical — see the
          // data_province name-resolution rule); tolerate legacy first_name/name
          // and the plain-string shape some payloads still pass.
          const name =
            typeof f === 'string'
              ? f
              : f.full_name || f.first_name || f.name || String(f);
          const linkedinUrl = typeof f === 'string' ? null : f.linkedin_url;
          return (
            <div key={i} style={{ marginBottom: '8px' }}>
              <span
                style={{
                  ...DS_TYPOGRAPHY.bodyMd,
                  color: DS_COLORS.text.primary,
                }}
              >
                {name}
              </span>
              {linkedinUrl && (
                <>
                  <span style={{ color: DS_COLORS.text.muted, margin: '0 4px' }}>
                    ·
                  </span>
                  <a
                    href={linkedinUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    style={{
                      ...DS_TYPOGRAPHY.bodySm,
                      color: DS_COLORS.text.brand,
                      textDecoration: 'underline',
                    }}
                  >
                    LinkedIn
                  </a>
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Fallback to the unverified research guesses ONLY for Tracxn deals, and only
  // when no Cowork-verified founders[] exist yet.
  if (isTracxn && foundersFromResearch && foundersFromResearch.length > 0) {
    return (
      <div data-testid="founders-section">
        {foundersFromResearch.map((f, i) => (
          <div key={i} style={{ marginBottom: '8px' }}>
            <span
              style={{
                ...DS_TYPOGRAPHY.bodyMd,
                color: DS_COLORS.text.primary,
              }}
            >
              {f.name}
            </span>
            {f.linkedin_url && (
              <>
                <span style={{ color: DS_COLORS.text.muted, margin: '0 4px' }}>
                  ·
                </span>
                <a
                  href={f.linkedin_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{
                    ...DS_TYPOGRAPHY.bodySm,
                    color: DS_COLORS.text.brand,
                    textDecoration: 'underline',
                  }}
                >
                  LinkedIn
                </a>
              </>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div data-testid="founders-section">
      <p
        style={{
          ...DS_TYPOGRAPHY.bodySm,
          color: DS_COLORS.text.muted,
          margin: 0,
          fontStyle: 'italic',
        }}
      >
        No founders listed — fallback to Tracxn page
      </p>
    </div>
  );
}

// ── Taxonomy section ──────────────────────────────────────────────────────────

function TaxonomySection({ selected }) {
  const payload = selected.payload || {};
  // Law §8.4: the sector is said only in the four state words, from the backend's `sector_state`; never the screen's own text.
  const sector = payload.sector_state?.compact_label || COCKPIT_LABELS.taxonomy.sectorNotChecked;
  const businessModel = payload.business_model;
  const customerType = payload.customer_type;
  const gtmMotion = payload.gtm_motion;

  const anyOpportunisticMissing =
    !businessModel || !customerType || !gtmMotion;

  return (
    <div data-testid="taxonomy-section">
      <TaxonomyRow label={COCKPIT_LABELS.taxonomy.sector} value={sector} />
      <TaxonomyRow label={COCKPIT_LABELS.taxonomy.businessModel} value={businessModel} />
      <TaxonomyRow label={COCKPIT_LABELS.taxonomy.customerType} value={customerType} />
      <TaxonomyRow label={COCKPIT_LABELS.taxonomy.gtmMotion} value={gtmMotion} />
      {anyOpportunisticMissing && (
        <p
          data-testid="taxonomy-footnote"
          style={{
            color: DS_COLORS.text.muted,
            fontSize: '11px',
            fontFamily: 'Inter, system-ui, sans-serif',
            fontStyle: 'italic',
            marginTop: '6px',
            marginBottom: 0,
          }}
        >
          {COCKPIT_LABELS.taxonomy.pendingFootnote}
        </p>
      )}
    </div>
  );
}

// ── Deck link section ─────────────────────────────────────────────────────────

function DeckLinkSection({ selected }) {
  const deckUrl =
    selected.payload?.deck_url ||
    selected.payload?.deal_screen?.deck_url;

  if (!deckUrl) {
    return (
      <div data-testid="deck-link-section">
        <p
          style={{
            ...DS_TYPOGRAPHY.bodySm,
            color: DS_COLORS.text.muted,
            margin: 0,
          }}
        >
          No deck attached
        </p>
      </div>
    );
  }

  return (
    <div data-testid="deck-link-section">
      <Button
        variant="outline"
        asChild
        style={{ minHeight: '44px', ...DS_TYPOGRAPHY.actionBtn }}
      >
        <a href={deckUrl} target="_blank" rel="noreferrer noopener">
          Open deck
        </a>
      </Button>
    </div>
  );
}

// ── Actions section ───────────────────────────────────────────────────────────

function ActionsSection({ selected, onRefresh }) {
  const [fcaPending, setFcaPending] = useState(false);
  const [passPending, setPassPending] = useState(false);
  const [passNoteOpen, setPassNoteOpen] = useState(false);
  // The chosen reason ID, not prose. Empty string means "nothing picked yet", which keeps
  // Confirm disabled — a pass is never recorded without a reason from the list.
  const [passReason, setPassReason] = useState('');
  // Dropdown open state — same controlled pattern as DealCard.jsx's Pass trigger
  // (WI-NIGHT-CLOSE-UI-RESTORE-AND-AMNESTY-STAGGER-001 Leg B2: this pane used to be the
  // only surface in the app rendering pass_reason as a raw native <select>, while the
  // preview pane's DealCard.jsx renders the SAME vocabulary as a DropdownMenu. One field,
  // two look-and-feels. This makes them one visual family.)
  const [passPickerOpen, setPassPickerOpen] = useState(false);

  const payload = selected.payload || {};
  const ds = payload.deal_screen || {};

  // FCA is enabled only when deal_screen has a call_decision
  const fcaEnabled = !!ds.call_decision;

  // Resolve startup_id for FCA call
  const startupId =
    payload.startup_id ||
    ds.startup_id ||
    selected.id;

  // Pass-with-note is for the deal-card types: a Tracxn deal (legacy card) or the generic first-look card (any source)
  const passEnabled =
    selected.work_type === WORK_TYPES.TRACXN_LIVEDEALS || selected.work_type === WORK_TYPES.FIRST_LOOK;
  // A first-look card whose company came through a Tracxn deal passes through the deal's own door (it records the pass
  // reason on the deal); a company from any other source has no deal, so its pass is the card's own reject, read back by id.
  const hasSourceDeal = Boolean(payload.deal_id || payload.tracxn_deal_id);

  // deal_id for pass endpoint
  const dealId = payload.deal_id || payload.tracxn_deal_id || selected.id;

  // Label of the currently chosen reason, for the DropdownMenu trigger's closed-state text.
  const passReasonLabel =
    PASS_REASON_ITEMS.find((r) => r.id === passReason)?.label || '';

  const handleSendToFCA = async () => {
    if (!fcaEnabled || fcaPending) return;
    setFcaPending(true);
    try {
      await request(`/api/fca/${encodeURIComponent(startupId)}/rerun/manual`, {
        method: 'POST',
      });
      fireToast('Sent to FCA');
      onRefresh?.();
    } catch (err) {
      console.error('[DealInspectorPane] FCA rerun failed', err);
      fireToast('FCA error', 'error');
    } finally {
      setFcaPending(false);
    }
  };

  const handlePassConfirm = async () => {
    if (!passEnabled || passPending) return;
    // A pass without a reason from the list is not recorded at all. The server refuses an
    // undeclared value too — this is the near guard, not the only one.
    if (!passReason) return;
    setPassPending(true);
    try {
      // The reason IS persisted: backend/routes/tracxn_livedeals.py writes pass_reason and
      // pass_reason_at onto tracxn_deals and mirrors both onto the dev_tasks card payload.
      // (This call used to carry a comment doubting that; it was wrong, and the doubt outlived
      // the gap. Verified against the route on 2026-08-06.)
      if (hasSourceDeal) {
        await request(`/api/tracxn/deals/${encodeURIComponent(dealId)}/pass`, {
          method: 'POST',
          body: JSON.stringify({ pass_reason: passReason }),
        });
      } else {
        await rejectCampaignTask(selected.id, passReasonLabel || passReason);
      }
      fireToast('Deal passed');
      setPassNoteOpen(false);
      setPassReason('');
      onRefresh?.();
    } catch (err) {
      console.error('[DealInspectorPane] Pass failed', err);
      // The server names WHY when it refuses an undeclared reason. Show that sentence rather
      // than a generic failure — a refusal the CEO cannot read is the same as no refusal.
      fireToast(err?.message || 'Pass error', 'error');
    } finally {
      setPassPending(false);
    }
  };

  const actionBtnBase = {
    minHeight: '44px',
    width: '100%',
    ...DS_TYPOGRAPHY.actionBtn,
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div data-testid="actions-section">
      {/* 1. Send to FCA */}
      <button
        data-testid="btn-send-fca"
        disabled={!fcaEnabled || fcaPending}
        onClick={handleSendToFCA}
        style={{
          ...actionBtnBase,
          background: fcaEnabled
            ? ACTION_BUTTONS.ai.background
            : DS_COLORS.surface.slate,
          color: fcaEnabled ? ACTION_BUTTONS.ai.color : DS_COLORS.text.muted,
          border: ACTION_BUTTONS.ai.border,
          borderRadius: ACTION_BUTTONS.ai.borderRadius,
          cursor:
            !fcaEnabled || fcaPending ? 'not-allowed' : 'pointer',
          opacity: !fcaEnabled || fcaPending ? 0.5 : 1,
        }}
        aria-disabled={!fcaEnabled || fcaPending}
      >
        {fcaPending ? 'Sending…' : 'Send to FCA'}
      </button>

      {/* 2. Pass with note */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              data-testid="btn-pass-with-note"
              disabled={!passEnabled || passPending}
              onClick={() =>
                passEnabled && !passPending && setPassNoteOpen((o) => !o)
              }
              style={{
                ...actionBtnBase,
                background: 'transparent',
                color: passEnabled
                  ? DS_COLORS.text.secondary
                  : DS_COLORS.text.muted,
                border: `1px solid ${DS_COLORS.border.default}`,
                borderRadius: ACTION_BUTTONS.rerun.borderRadius,
                cursor: !passEnabled || passPending ? 'not-allowed' : 'pointer',
                opacity: !passEnabled || passPending ? 0.5 : 1,
              }}
              aria-disabled={!passEnabled || passPending}
            >
              {COCKPIT_LABELS.inspectorActions.passWithReason}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {/* This used to read "Note captured in UI; server-side persistence pending."
                That was wrong, and the doubt outlived the gap: the route has written
                pass_reason and pass_reason_at to tracxn_deals, and mirrored them onto the
                card payload, since WI-SHARED-DEAL-CARD-001. Verified against the route
                2026-08-06. A tooltip that tells the CEO his input may be discarded, when it
                is not, teaches him to distrust a working control. */}
            <p style={{ fontSize: '12px', maxWidth: '220px' }}>
              Recorded on the deal, so passes can be counted by reason.
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Inline pass note form */}
      {passNoteOpen && passEnabled && (
        <div
          data-testid="pass-note-form"
          style={{
            marginBottom: '12px',
            padding: '12px',
            background: DS_COLORS.surface.slate,
            borderRadius: '8px',
            border: `1px solid ${DS_COLORS.border.default}`,
          }}
        >
          {PASS_REASON_ITEMS.length === 0 ? (
            // A CLASSIFICATION FIELD WITH NO LIST REFUSES — it never falls back to free text.
            // This is the visible half of the same rule the server enforces; between them
            // there is no path that stores an unclassifiable reason.
            <p
              data-testid="pass-reason-unavailable"
              style={{
                ...DS_TYPOGRAPHY.body,
                fontSize: '16px',
                color: DS_COLORS.text.secondary,
                margin: '0 0 10px',
              }}
            >
              {COCKPIT_LABELS.inspectorActions.passReasonListMissing}
            </p>
          ) : (
            <DropdownMenu open={passPickerOpen} onOpenChange={setPassPickerOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="pass-reason-select"
                  aria-label={COCKPIT_LABELS.inspectorActions.passReasonAriaLabel}
                  style={{
                    ...DS_TYPOGRAPHY.body,
                    width: '100%',
                    minHeight: '44px',
                    fontSize: '16px',
                    marginBottom: '10px',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: `1px solid ${DS_COLORS.border.default}`,
                    background: DS_COLORS.surface.white,
                    color: passReason
                      ? DS_COLORS.text.primary
                      : DS_COLORS.text.muted,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                  }}
                >
                  {passReasonLabel || COCKPIT_LABELS.inspectorActions.passReasonPlaceholder}
                  <CaretDown size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                style={{ width: 'var(--radix-dropdown-menu-trigger-width)' }}
              >
                {PASS_REASON_ITEMS.map((reason) => (
                  <DropdownMenuItem
                    key={reason.id}
                    data-testid={`pass-reason-option-${reason.id}`}
                    onSelect={() => setPassReason(reason.id)}
                  >
                    {reason.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setPassNoteOpen(false);
                setPassReason('');
              }}
              style={{
                ...DS_TYPOGRAPHY.actionBtn,
                background: 'transparent',
                border: `1px solid ${DS_COLORS.border.default}`,
                borderRadius: '6px',
                padding: '6px 14px',
                cursor: 'pointer',
                color: DS_COLORS.text.secondary,
                minHeight: '44px',
              }}
            >
              Cancel
            </button>
            <button
              data-testid="pass-note-confirm"
              onClick={handlePassConfirm}
              // No reason picked -> nothing to record. Disabled rather than sending an empty
              // string, which the server would store as "passed with no reason at all".
              disabled={passPending || !passReason}
              style={{
                ...DS_TYPOGRAPHY.actionBtn,
                background: DS_COLORS.text.brand,
                color: DS_COLORS.surface.white,
                border: 'none',
                borderRadius: '6px',
                padding: '6px 14px',
                cursor: passPending ? 'wait' : !passReason ? 'not-allowed' : 'pointer',
                opacity: passPending || !passReason ? 0.6 : 1,
                minHeight: '44px',
              }}
              aria-disabled={passPending || !passReason}
            >
              {passPending ? 'Passing…' : 'Confirm'}
            </button>
          </div>
        </div>
      )}

      {/* 3. Schedule founder call — DISABLED */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              data-testid="btn-schedule-call"
              disabled
              aria-disabled="true"
              style={{
                ...actionBtnBase,
                background: 'transparent',
                color: DS_COLORS.text.muted,
                border: `1px solid ${DS_COLORS.border.default}`,
                borderRadius: ACTION_BUTTONS.rerun.borderRadius,
                cursor: 'not-allowed',
                opacity: 0.5,
              }}
            >
              Schedule founder call
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p style={{ fontSize: '12px' }}>Coming in next WI</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* 4. Park — DISABLED */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              data-testid="btn-park"
              disabled
              aria-disabled="true"
              style={{
                ...actionBtnBase,
                background: 'transparent',
                color: DS_COLORS.text.muted,
                border: `1px solid ${DS_COLORS.border.default}`,
                borderRadius: ACTION_BUTTONS.rerun.borderRadius,
                cursor: 'not-allowed',
                opacity: 0.5,
                marginBottom: 0,
              }}
            >
              Park
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p style={{ fontSize: '12px' }}>Coming in next WI</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function DealInspectorPane({ selected, onRefresh }) {
  const sectionStyle = { marginBottom: '20px' };

  return (
    <div
      data-testid="deal-inspector-pane"
      data-selected-id={selected?.id || 'none'}
      role="region"
      aria-label={COCKPIT_LABELS.aria.inspector}
      style={{
        width: 320,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        background: DS_COLORS.surface.white,
        borderLeft: `1px solid ${DS_COLORS.border.default}`,
        overflow: 'hidden',
      }}
    >
      {!selected ? (
        <EmptyState />
      ) : (
        <ScrollArea style={{ flex: 1, height: '100%' }}>
          <div style={{ padding: '24px' }}>

            {/* Founders */}
            <div style={sectionStyle}>
              <MicroLabel>Founders</MicroLabel>
              <FoundersSection selected={selected} />
            </div>

            <Separator style={{ marginBottom: '16px' }} />

            {/* Taxonomy */}
            <div style={sectionStyle}>
              <MicroLabel>Taxonomy</MicroLabel>
              <TaxonomySection selected={selected} />
            </div>

            <Separator style={{ marginBottom: '16px' }} />

            {/* Deck */}
            <div style={sectionStyle}>
              <MicroLabel>Deck</MicroLabel>
              <DeckLinkSection selected={selected} />
            </div>

            <Separator style={{ marginBottom: '16px' }} />

            {/* KB writes */}
            <div style={sectionStyle}>
              <MicroLabel>Knowledge Base</MicroLabel>
              <p
                data-testid="kb-writes-placeholder"
                style={{
                  ...DS_TYPOGRAPHY.bodySm,
                  color: DS_COLORS.text.muted,
                  margin: 0,
                }}
              >
                {/* DECISION: Placeholder per spec — no /api/kb/by-startup endpoint exists yet.
                    Full implementation flagged as scope gap G5 in Stage 2 plan. */}
                KB writes: —
              </p>
            </div>

            <Separator style={{ marginBottom: '16px' }} />

            {/* Actions */}
            <div>
              <MicroLabel>Actions</MicroLabel>
              <ActionsSection selected={selected} onRefresh={onRefresh} />
            </div>
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
